import React, { useState, useRef, useEffect } from 'react';
import { Send, Smile, Paperclip, X, Image, File, FileText } from 'lucide-react';
import Button from '../Button';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useAuthStore } from '../../store/authStore';
import { StorageService, useFileUpload, type StorageFile } from '../../lib/storage';
import { useAttachments } from '../../utils/attachmentUtils';
import { useParams, useLocation } from 'react-router-dom';

interface MessageComposerProps {
  onSend: (content: string, attachments?: StorageFile[]) => void;
  onTyping?: () => void;
  disabled?: boolean;
  placeholder?: string;
  initialValue?: string;
  updateTypingStatus?: (isTyping: boolean) => void;
  conversationId?: string;
}

export default function MessageComposer({
  onSend,
  onTyping,
  disabled = false,
  placeholder = 'Type a message...',
  initialValue = '',
  updateTypingStatus,
  conversationId
}: MessageComposerProps) {
  const { user } = useAuthStore();
  const { id: paramConversationId } = useParams();
  const location = useLocation();
  const { uploadFiles, isUploading, uploadError, clearError } = useFileUpload();
  
  // Use prop conversationId or fall back to URL param or search params
  const currentConversationId = conversationId || paramConversationId || new URLSearchParams(location.search).get('id');
  
  console.log('MessageComposer conversation ID sources:', {
    prop: conversationId,
    param: paramConversationId,
    search: new URLSearchParams(location.search).get('id'),
    final: currentConversationId,
    location: location.pathname + location.search
  });
  
  const [content, setContent] = useState(initialValue);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachments, setAttachments] = useState<StorageFile[]>([]);
  const [messageError, setMessageError] = useState<string | null>(null);
  
  // Use the same hook as MessageList to process attachments with signed URLs
  const { processedAttachments, isLoading: attachmentsLoading } = useAttachments(attachments, 'conversation');
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        updateTypingStatus?.(false);
      }
    };
  }, [updateTypingStatus]);

  const handleTyping = () => {
    if (onTyping) {
      onTyping();
    }
    
    if (updateTypingStatus) {
      updateTypingStatus(true);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        updateTypingStatus(false);
      }, 3000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    const trimmedContent = content.trim();
    if ((trimmedContent || attachments.length > 0) && !disabled && !isUploading) {
      setMessageError(null);
      
      // Create a promise to handle the async operation
      Promise.resolve(onSend(trimmedContent, attachments.length > 0 ? attachments : undefined))
        .then(() => {
          setContent('');
          setAttachments([]);
          
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            updateTypingStatus?.(false);
          }
        })
        .catch((error: any) => {
          console.error('Error sending message:', error);
          setMessageError(error.message || 'Failed to send message');
        });
    }
  };

  // Clear message error when user starts typing
  const handleContentChange = (value: string) => {
    setContent(value);
    if (messageError) {
      setMessageError(null);
    }
    handleTyping();
  };

  // Clear message error when attachments change
  useEffect(() => {
    if (messageError && attachments.length > 0) {
      setMessageError(null);
    }
  }, [attachments.length, messageError]);

  const handleEmojiSelect = (emoji: any) => {
    setContent(prev => prev + emoji.native);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length || !user || !currentConversationId) {
      console.error('Missing requirements for file upload:', {
        files: !!files,
        filesLength: files?.length,
        user: !!user,
        conversationId: !!currentConversationId
      });
      return;
    }

    try {
      clearError();
      const fileArray = Array.from(files);
      
      console.log('Uploading files:', fileArray.map(f => f.name));
      console.log('Upload config:', {
        userId: user.id,
        contextId: currentConversationId,
        contextType: 'conversation'
      });
      
      const uploadedFiles = await uploadFiles(fileArray, {
        userId: user.id,
        contextId: currentConversationId,
        contextType: 'conversation'
      });

      console.log('Successfully uploaded files:', uploadedFiles);
      setAttachments(prev => [...prev, ...uploadedFiles]);
    } catch (error: any) {
      console.error('Upload error:', error);
      // Error is handled by the useFileUpload hook
    } finally {
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = async (index: number) => {
    try {
      const attachment = attachments[index];
      
      // Remove from storage
      await StorageService.removeFile('message_attachments', attachment.path);

      // Remove from local state
      setAttachments(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error removing attachment:', error);
      // Still remove from local state even if storage removal fails
      setAttachments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [content]);

  const renderAttachmentPreview = (attachment: any, index: number) => {
    const isImage = attachment.type.startsWith('image/');
    const isPdf = attachment.type === 'application/pdf';
    const isText = attachment.type.includes('text') || attachment.type.includes('document');
    const displayUrl = attachment.signedUrl || attachment.url;
    
    return (
      <div key={index} className="relative group">
        <div className="flex items-center p-2 bg-neutral rounded-lg">
          {isImage && displayUrl ? (
            <div className="w-12 h-12 mr-2 relative">
              <img 
                src={displayUrl} 
                alt={attachment.name} 
                className="w-full h-full object-cover rounded-md"
                onError={(e) => {
                  // Fallback to file icon if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden w-full h-full flex items-center justify-center bg-surface rounded-md">
                <Image className="w-5 h-5 text-neutral" />
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 flex items-center justify-center mr-2 bg-surface rounded-md">
              {isPdf ? (
                <FileText className="w-5 h-5 text-secondary-dark" />
              ) : isText ? (
                <File className="w-5 h-5 text-accent" />
              ) : (
                <Paperclip className="w-5 h-5 text-neutral" />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-primary truncate">{attachment.name}</div>
            <div className="text-xs text-neutral">
              {(attachment.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <button
            type="button"
            onClick={() => removeAttachment(index)}
            className="p-1 text-surface-light hover:text-primary rounded-full"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 border-t border-neutral border-opacity-10">
      {(uploadError || messageError) && (
        <div className="mb-3 p-2 rounded-lg bg-secondary-dark bg-opacity-10 text-secondary-dark text-sm">
          {messageError || uploadError}
        </div>
      )}

      {/* Show loading state while attachments are being processed */}
      {attachmentsLoading && attachments.length > 0 && (
        <div className="mb-3 p-2 rounded-lg bg-surface-light">
          <div className="animate-pulse text-sm text-neutral">Processing attachments...</div>
        </div>
      )}

      {/* Render attachment previews using processed attachments with signed URLs */}
      {processedAttachments.length > 0 && (
        <div className="mb-3 space-y-2">
          {processedAttachments.map((attachment, index) => renderAttachmentPreview(attachment, index))}
        </div>
      )}
      
      <div className="flex items-end space-x-2">
        <div className="flex-1 bg-neutral text-surface-light rounded-full">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => {
              handleContentChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isUploading}
            className="w-full min-h-[44px] max-h-[150px] p-3 pl-4 bg-transparent border-none rounded-xl resize-none focus:ring-0 placeholder:text-surface-light focus:outline-none focus:ring-0 "
            rows={1}
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="relative">
            <button
              type="button"
              onClick={handleFileClick}
              className="p-2 text-neutral hover:text-primary transition-colors"
              disabled={disabled || isUploading}
              title="Attach file"
            >
              <Paperclip className={`w-5 h-5 ${isUploading ? 'animate-pulse' : ''}`} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={handleFileChange}
              disabled={isUploading}
              multiple
            />
          </div>            
          
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={disabled || isUploading || (!content.trim() && attachments.length === 0)}
            className="h-[44px] w-[44px] p-0 flex items-center justify-center text-surface-light"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}